import utils from '@bigcommerce/stencil-utils';
import { showAlertModal } from '../../global/modal';
import openSlideCart from '../../common/open-slide-cart';
import { getCartPageOvrPricing } from '../shipping/ovr-shipping';
import { fireAddToCart } from './ga4-events';
import { getMotherShipSku } from './productSkuMap';
/**
 * ------------------------
 * Helpers
 * ------------------------
 */

// Promisify stencil-utils callbacks
function cartItemAdd(formData) {
    return new Promise((resolve, reject) => {
        utils.api.cart.itemAdd(formData, (err, response) => {
            if (err || response?.data?.error) {
                reject(err || response.data.error);
                return;
            }
            // Some failure modes (e.g. product requires unselected options → 404)
            // return { data: [] } with no err and no data.error. Reject so the
            // catch in addToCartSlideOut shows a graceful modal instead of
            // letting downstream code throw on undefined cart_item.
            if (!response?.data?.cart_item) {
                reject('Could not add this item to your cart. Please open the product page to select options.');
                return;
            }
            resolve(response);
        });
    });
}

function cartGetContent(options) {
    return new Promise((resolve, reject) => {
        utils.api.cart.getContent(options, (err, response) => {
            if (err) reject(err);
            else resolve(response);
        });
    });
}

function cartGet() {
    return new Promise((resolve, reject) => {
        utils.api.cart.getCart({}, (err, response) => {
            if (err) reject(err);
            else resolve(response);
        });
    });
}

function filterEmptyFilesFromForm(formData) {
    try {
        for (const [key, val] of formData) {
            if (val instanceof File && !val.name && !val.size) {
                formData.delete(key);
            }
        }
    } catch (e) {
        console.error(e); // eslint-disable-line no-console
    }
    return formData;
}

function sanitizeErrorMessage(errorMessage) {
    const tmp = document.createElement('div');
    tmp.innerHTML = errorMessage;
    return tmp.textContent || tmp.innerText || 'Unknown error';
}

async function updateSideCart() {
    const $sideCart = $('.side-cart-content');
    const $sideCartDetails = $('.side-cart-details');
    const $sideCartActions = $('.side-cart-actions');

    const options = {
        template: {
            template: 'f/b2b/side-cart-content',
            details: 'f/b2b/side-cart-details',
            actions: 'f/b2b/side-cart-actions',
            itemcount: 'f/cart/item-count',
        },
    };

    const response = await cartGetContent(options);

    if (response?.itemcount > 0) {
        $sideCart.html(response.template);
        $sideCartDetails.html(response.details);
        $sideCartActions.html(response.actions);
        $('.cart-quantity').html(response.itemcount);

        if (typeof itemInCart === 'function') {
            itemInCart();
        }
    }

    $('.side-cart-loading').css('display', 'none');
    $('.side-cart-item').css('opacity', '1');
}

/**
 * ------------------------
 * Main entry
 * ------------------------
 */
export async function addToCartSlideOut(productId, optionIds = [], optionValueIds = [], quantity = 1, sku = '') {
    try {
        // Build the form
        const form = document.createElement('form');
        form.method = 'post';
        form.action = '/cart.php';

        form.innerHTML = `
            <input type="hidden" name="action" value="add">
            <input type="hidden" name="product_id" value="${productId}">
            <input class="form-input form-input-qty" name="qty[]" type="tel" value="${quantity}" min="1">
        `;

        // Add options
        optionIds.forEach((optId, i) => {
            const valueId = optionValueIds[i];
            const wrapper = document.createElement('div');
            wrapper.innerHTML = `
                <div class="form-field" data-product-attribute="radio">
                    <label class="form-label form-label--alternate form-label--inlineSmall">
                        Option ${optId}:
                        <span data-option-value="">${valueId}</span>
                        <small class="required-label">Required</small>
                    </label>
                    <input class="form-radio" type="radio" 
                           name="attribute[${optId}]" 
                           id="acc_attribute_radio_${optId}_${valueId}" 
                           value="${valueId}" 
                           required checked>
                    <label class="form-option" for="acc_attribute_radio_${optId}_${valueId}" 
                           data-product-attribute-value="${valueId}">
                        <span class="form-option-variant">${valueId}</span>
                    </label>
                </div>
            `;
            form.appendChild(wrapper);
        });

        // Submit to API
        const formData = filterEmptyFilesFromForm(new FormData(form));
        const response = await cartItemAdd(formData);
        getAddToCartData(response, productId, quantity, sku);


        const cartData = response.data;
        // Match the line item for this product (first match by product_id)
        const lineItem = (cartData.line_items || [])
            .find(li => li.product_id === cartData.cart_item?.product_id)
            || cartData.line_items?.[0]
            || {};
        // Fall back to DOM title if context product is unavailable
        //const productName = product?.title || product?.name|| document.querySelector('h1.productView-title, .productView-title')?.textContent?.trim()|| '';
        //const productSku = product?.sku || '';
        const enrichedCartItem = {
            ...cartData.cart_item,
            name: lineItem.product_name || cartData.cart_item.name,
            product_sku: getMotherShipSku(cartData.cart_item?.product_id) || lineItem.sku || cartData.cart_item.sku,
            variant_sku: lineItem.sku || cartData.cart_item.sku,
            sale_price: lineItem.sale_price ?? cartData.cart_item.sale_price
                ?? cartData.cart_item.price_ex_tax
                ?? cartData.product_value ?? 0,
            base_price: lineItem.base_price ?? null,
            retail_price: lineItem.retail_price ?? null,
            purchase_price: lineItem.purchase_price ?? null,
            quantity: lineItem.quantity ?? cartData.cart_item.quantity ?? 1,
            discount: lineItem.discount ?? 0,
            brand_name: lineItem.brand_name || '',
            category_names: lineItem.category_names || [],
            variant_id: lineItem.variant_id ?? null,
            currency: lineItem.currency || cartData.currency || 'USD',
            price: lineItem.purchase_price ?? 0,
        };
        fireAddToCart(enrichedCartItem, enrichedCartItem.currency);

        // Enrich with pricing overrides
        const sendURL = typeof onlineServiceUrl !== 'undefined' ? onlineServiceUrl : 'https://api.storesupply.com/';
        getCartPageOvrPricing(response.data.line_items, sendURL, true);

        // Refresh full cart data (pricing, totals, etc.)
        const fullCart = await cartGet();
        getCartPageOvrPricing(fullCart?.lineItems?.physicalItems, sendURL, true);

        // Update UI
        document.prevFocus = document.activeElement || document.body;
        await updateSideCart();
        openSlideCart();

    } catch (error) {
        showAlertModal(sanitizeErrorMessage(error));
    }
}
